<script setup lang="ts">
/**
 * PageSettingsCampaignPicker ([D-297], 16-09-2026): renderer §46-felttypen
 * 'campaigns' i tandhjuls-draweren og på Indstillinger → Sideindstillinger.
 *
 * Kampagnerne vælges på platformens ID, aldrig på navnet: bureauet omdøber og
 * samler kampagner, og et navne-match fejler da tavst. Listen hentes fra
 * GET /api/page-settings/campaign-options (view-grant til siden) med navn,
 * status, seneste forbrug og forbrug de seneste 90 dage, så man kan se hvilken
 * kampagne der faktisk bruger penge. De valgte står altid øverst; øvrige vises
 * som standard kun med forbrug de seneste 90 dage (resten bag én knap), og der
 * kan søges på navn eller ID.
 *
 * Kladden lever hos forælderen: hver ændring udsender hele den nye liste, så
 * Gem/Fortryd virker som for de andre felttyper.
 */

import { computed, ref, watch } from 'vue';
import {
  PAGE_SETTING_CAMPAIGNS_MAX,
  adPlatformLabel,
  type AdCampaignOptionDto,
  type PageSettingFieldDef,
} from '@cockpit/shared';

import { formatDateDa } from '../lib/datetime';
import { fetchJson } from '../lib/http';
import { kr } from '../lib/marketing-format';

const props = defineProps<{
  routePath: string;
  field: PageSettingFieldDef;
  value: string[];
  disabled: boolean;
}>();

const emit = defineEmits<{ (e: 'update', value: string[]): void }>();

const options = ref<AdCampaignOptionDto[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const query = ref('');
const showAll = ref(false);

async function load(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    const res = await fetchJson<{ campaigns: AdCampaignOptionDto[] }>(
      `/api/page-settings/campaign-options?routePath=${encodeURIComponent(props.routePath)}&field=${encodeURIComponent(props.field.key)}`,
    );
    options.value = res.campaigns;
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err);
  } finally {
    loading.value = false;
  }
}
watch(() => [props.routePath, props.field.key], () => void load(), { immediate: true });

const byKey = computed(() => new Map(options.value.map((o) => [o.key, o])));
const selectedSet = computed(() => new Set(props.value));

const bySpend = (a: AdCampaignOptionDto, b: AdCampaignOptionDto): number =>
  b.spend90Kr - a.spend90Kr ||
  (b.lastSpendDate ?? '').localeCompare(a.lastSpendDate ?? '') ||
  (a.name ?? '').localeCompare(b.name ?? '', 'da');

/** De valgte, også dem der (endnu) ikke findes i databasen. */
const selectedRows = computed(() =>
  props.value
    .map((k) => byKey.value.get(k) ?? { key: k, unknown: true as const })
    .sort((a, b) => ('unknown' in a ? 1 : 0) - ('unknown' in b ? 1 : 0) || ('unknown' in a || 'unknown' in b ? 0 : bySpend(a, b))),
);

const matchesQuery = (o: AdCampaignOptionDto): boolean => {
  const q = query.value.trim().toLowerCase();
  if (!q) return true;
  return (o.name ?? '').toLowerCase().includes(q) || o.externalCampaignId.includes(q);
};

const others = computed(() => options.value.filter((o) => !selectedSet.value.has(o.key) && matchesQuery(o)));
const othersWithoutSpend = computed(() => others.value.filter((o) => o.spend90Kr <= 0).length);
const otherRows = computed(() =>
  others.value.filter((o) => showAll.value || query.value.trim() !== '' || o.spend90Kr > 0).sort(bySpend),
);

function toggle(key: string, checked: boolean): void {
  if (props.disabled) return;
  if (checked) {
    if (props.value.includes(key) || props.value.length >= PAGE_SETTING_CAMPAIGNS_MAX) return;
    emit('update', [...props.value, key]);
  } else {
    emit('update', props.value.filter((k) => k !== key));
  }
}

/** Platformenes kampagne-status på dansk (Google: ENABLED/PAUSED/REMOVED, Meta: ACTIVE/PAUSED, Microsoft: Active/Paused). */
const STATUS_DA: Record<string, string> = { enabled: 'aktiv', active: 'aktiv', paused: 'pauset', removed: 'fjernet', deleted: 'slettet', archived: 'arkiveret' };

function subLine(o: AdCampaignOptionDto): string {
  const parts = [adPlatformLabel(o.provider)];
  if (o.status) parts.push(STATUS_DA[o.status.toLowerCase()] ?? o.status.toLowerCase());
  parts.push(o.lastSpendDate ? `senest forbrug ${formatDateDa(o.lastSpendDate, false)}` : 'intet forbrug');
  if (o.spend90Kr > 0) parts.push(`${kr(o.spend90Kr)} på 90 dage`);
  return parts.join(' · ');
}
</script>

<template>
  <div class="flex flex-col gap-2" :data-testid="`ps-campaigns-${props.field.key}`">
    <p class="text-[11px] text-faint">
      {{ props.value.length }} valgt. Platforme: {{ (props.field.providers ?? []).map(adPlatformLabel).join(', ') }}.
    </p>

    <p v-if="error" class="text-[11px] text-red">Kampagnerne kunne ikke hentes: {{ error }}</p>
    <p v-else-if="loading && options.length === 0" class="text-[11px] text-faint">Henter kampagner …</p>

    <!-- Valgte -->
    <ul v-if="selectedRows.length > 0" class="flex flex-col gap-1" data-testid="ps-campaigns-selected">
      <li
        v-for="r in selectedRows"
        :key="r.key"
        class="flex items-start gap-2 p-1.5 rounded-lg border border-line bg-subtle/40"
        data-testid="ps-campaign-row"
        :data-key="r.key"
        data-selected="true"
      >
        <input
          type="checkbox"
          class="mt-0.5 rounded border-line shrink-0"
          checked
          :disabled="props.disabled"
          :aria-label="`Fravælg ${'unknown' in r ? r.key : (r.name ?? r.key)}`"
          @change="toggle(r.key, ($event.target as HTMLInputElement).checked)"
        />
        <span class="min-w-0">
          <template v-if="'unknown' in r">
            <span class="block text-xs text-ink break-words">Ukendt kampagne</span>
            <span class="block text-[11px] text-amber break-all" v-tip="'Kampagnen findes ikke i databasen. Den kan være slettet hos platformen, eller databasen mangler platformens data (fx en lokal kopi).'">{{ r.key }}</span>
          </template>
          <template v-else>
            <span class="block text-xs text-ink break-words">{{ r.name ?? r.externalCampaignId }}</span>
            <span class="block text-[11px] text-faint">{{ subLine(r) }}</span>
          </template>
        </span>
      </li>
    </ul>

    <input
      v-model="query"
      type="search"
      placeholder="Søg kampagne eller ID"
      aria-label="Søg kampagne eller ID"
      class="px-2 py-1 rounded-lg border border-line bg-surface text-xs text-ink focus:outline-none focus:border-ink/40"
      data-testid="ps-campaigns-search"
    />

    <!-- Øvrige -->
    <ul v-if="otherRows.length > 0" class="flex flex-col gap-1" data-testid="ps-campaigns-others">
      <li
        v-for="o in otherRows"
        :key="o.key"
        class="flex items-start gap-2 p-1.5 rounded-lg border border-line"
        data-testid="ps-campaign-row"
        :data-key="o.key"
        data-selected="false"
      >
        <input
          type="checkbox"
          class="mt-0.5 rounded border-line shrink-0"
          :disabled="props.disabled || props.value.length >= PAGE_SETTING_CAMPAIGNS_MAX"
          :aria-label="`Vælg ${o.name ?? o.key}`"
          @change="toggle(o.key, ($event.target as HTMLInputElement).checked)"
        />
        <span class="min-w-0">
          <span class="block text-xs text-ink break-words">{{ o.name ?? o.externalCampaignId }}</span>
          <span class="block text-[11px] text-faint">{{ subLine(o) }}</span>
        </span>
      </li>
    </ul>
    <p v-else-if="!loading && !error" class="text-[11px] text-faint">
      {{ query.trim() ? 'Ingen kampagner matcher søgningen.' : 'Ingen øvrige kampagner med forbrug de seneste 90 dage.' }}
    </p>

    <button
      v-if="!query.trim() && othersWithoutSpend > 0"
      type="button"
      class="self-start px-2.5 py-1 rounded-lg border border-line text-xs text-ink hover:bg-hover transition-colors"
      data-testid="ps-campaigns-show-all"
      @click="showAll = !showAll"
    >
      {{ showAll ? 'Skjul kampagner uden forbrug' : `Vis også ${othersWithoutSpend} uden forbrug de seneste 90 dage` }}
    </button>
  </div>
</template>
